import { useContext, useEffect, useState } from 'react'
import { store } from '../store'
import { getAccessToken, getIsAuthenticated } from '../selectors/auth'

const useFetch = <Response>({
  url,
  needsAuthorization,
  headers,
  options,
  parameters,
}: {
  url: string,
  needsAuthorization?: boolean,
  headers?: { [key: string]: string },
  options?: { [key: string]: string },
  parameters?: { [key: string]: string },
}): {
    isFetchLoading: boolean,
    fetchResponse?: Response,
    fetchError: Error | boolean,
  } => {
  const [fetchResponse, setFetchResponse] = useState()
  const [isFetchLoading, setFetchLoading] = useState(false)
  const [fetchError, setFetchError] = useState<Error | boolean>(false)
  const { state } = useContext(store)
  const accessToken = getAccessToken(state)
  const isAuthenticated = getIsAuthenticated(state)

  useEffect(() => {
    setFetchResponse(undefined)

    const authHeaderForLocalDev = (
      process.env.ENVIRONMENT === 'local'
      && needsAuthorization
      && accessToken) ? { authorization: `Bearer ${accessToken}` } : undefined

    if (needsAuthorization && !isAuthenticated) {
      setFetchError(new Error('Authorization access token is not present'))
    } else if (url) {
      const abortController = new global.AbortController()
      const { signal } = abortController

      const urlToFetch = new URL(url)

      if (parameters) {
        urlToFetch.search = (new URLSearchParams(parameters)).toString()
      }

      const fetchData = async () => {
        try {
          setFetchLoading(true)

          const res = await global.fetch(urlToFetch.toString(), {
            ...options,
            credentials: 'include',
            headers: {
              ...headers,
              ...authHeaderForLocalDev,
            },
          })

          const json = await res.json()

          if (!signal.aborted) {
            setFetchResponse(json)
          }
        } catch (error) {
          if (!signal.aborted) {
            setFetchError(error as Error)
          }
        } finally {
          if (!signal.aborted) {
            setFetchLoading(false)
          }
        }
      }

      fetchData()
    }
  }, [url]) // eslint-disable-line react-hooks/exhaustive-deps

  return { isFetchLoading, fetchResponse, fetchError }
}

export { useFetch }
